<template>
  <div class="menu__container">
    <el-aside :class="asideWidthClass">
      <logo :collapsed="collapse" />
      <el-menu
          :collapse="collapse"
          mode="vertical"
          :router="true"
          :default-active="defaultActiveMenuItem">
        <section-separator />

        <template v-for="(menuItem, index) in filteredMenuItems">
          <!-- Insert Account Info separator before CustomerProfile item -->
          <section-separator
              v-if="menuItem.name === 'CustomerProfile'"
              :key="'separator-' + menuItem.name"
              :title="'Account Info'"
              :padded="collapse" />

          <div
              :key="menuItem.name"
              class="border-box">
            <div
                v-if="collapse"
                :index="menuItem.index.toString()"
                class="collapsed__menu-item__container flex__center">
              <menu-item-collapsed
                  :index="menuItem.index.toString()"
                  :icon="menuItem.icon"
                  :icon-hovered="menuItem.iconHovered"
                  :route="{name: menuItem.name, params: {id: customer.id}}"
                  :description="$t(menuItem.i18n)" />
            </div>
            <menu-item
                v-else
                :index="menuItem.index.toString()"
                :icon="menuItem.icon"
                :icon-hovered="menuItem.iconHovered"
                :route="{name: menuItem.name, params: {id: customer.id}}"
                :description="$t(menuItem.i18n)" />
          </div>
        </template>

        <section-separator v-if="!collapse" />
        
        <!-- Feature Requests Link -->
        <div class="border-box">
          <div
              v-if="collapse"
              class="collapsed__menu-item__container flex__center">
            <menu-item-collapsed
                index="feature-requests"
                :icon="require('@/assets/icons/new-ui/left-navbar/requests.svg')"
                :icon-hovered="require('@/assets/icons/new-ui/left-navbar/requests__hovered.svg')"
                :route="{name: 'FeatureRequests'}"
                description="Feature Requests" />
          </div>
          <menu-item
              v-else
              index="feature-requests"
              :icon="require('@/assets/icons/new-ui/left-navbar/requests.svg')"
              :icon-hovered="require('@/assets/icons/new-ui/left-navbar/requests__hovered.svg')"
              :route="{name: 'FeatureRequests'}"
              description="Feature Requests" />
        </div>
      </el-menu>
      <menu-footer @collapseNotification="setCollapse" />
    </el-aside>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import MenuItem from "./MenuItem";
import SectionSeparator from "./SectionSeparator";
import Logo from "./Logo";
import Footer from "./MenuFooter";
import MenuFooter from "./MenuFooter";
import MenuItemCollapsed from "./MenuItemCollapsed";
import permission from "@/mixins/permission";

const { mapGetters: accountGetters } = createNamespacedHelpers("db/account");
const { mapActions: uiActions } = createNamespacedHelpers("ui");

export default {
  name: "NewMainLeftMenu",
  components: {
    MenuItemCollapsed,
    MenuFooter,
    Footer,
    SectionSeparator,
    MenuItem,
    Logo,
  },
  mixins: [permission],
  data() {
    return {
      collapse: false,
      defaultActiveMenuItem: "1",
      routeNames: {
        Dashboard: "Dashboard",
        CustomerProfile: "CustomerProfile",
        Devices: "Devices",
        Device: "Devices",
        CustomerRequests: "CustomerRequests",
        CustomerHistory: "CustomerHistory",
        OrderWidget: "OrderWidget",
        CustomerTransfers: "CustomerTransfers",
        DeviceTransfers: "CustomerTransfers",
      },
    };
  },
  computed: {
    ...accountGetters(["customer"]),
    asideWidthClass() {
      if (this.collapse) {
        return "aside__collapsed-width";
      }
      return "aside__normal-width";
    },
    requestsRouteName() {
      if (this.hasPermission("NewUI", "OrderWidget")) return "OrderWidget";
      return "CustomerRequests";
    },
    filteredMenuItems() {
      return this.menuItems;
    },
    menuItems() {
      return [
        {
          index: 1,
          name: "Dashboard",
          id: "",
          icon: require("@/assets/icons/new-ui/left-navbar/dashboard.svg"),
          iconHovered: require("@/assets/icons/new-ui/left-navbar/dashboard__hovered.svg"),
          i18n: "common.dashboard",
        },
        {
          index: 2,
          name: "Devices",
          id: "",
          icon: require("@/assets/icons/new-ui/left-navbar/devices.svg"),
          iconHovered: require("@/assets/icons/new-ui/left-navbar/devices__hovered.svg"),
          i18n: "common.devices",
        },
        {
          index: 3,
          name: "CustomerProfile",
          id: "",
          icon: require("@/assets/icons/new-ui/left-navbar/account.svg"),
          iconHovered: require("@/assets/icons/new-ui/left-navbar/account__hovered.svg"),
          i18n: "common.account",
          section: "account-info", // Mark as first item in Account Info section
        },
        {
          index: 4,
          name: "CustomerHistory",
          id: "",
          icon: require("@/assets/icons/new-ui/left-navbar/history.svg"),
          iconHovered: require("@/assets/icons/new-ui/left-navbar/history__hovered.svg"),
          i18n: "common.history",
        },
        {
          index: 5,
          name: this.requestsRouteName,
          id: "",
          icon: require("@/assets/icons/new-ui/left-navbar/requests.svg"),
          iconHovered: require("@/assets/icons/new-ui/left-navbar/requests__hovered.svg"),
          i18n: "common.requests",
        },
        {
          index: 6,
          name: "CustomerTransfers",
          id: "",
          icon: require("@/assets/icons/new-ui/left-navbar/devices.svg"),
          iconHovered: require("@/assets/icons/new-ui/left-navbar/devices__hovered.svg"),
          i18n: "common.transfers",
        },
      ];
    },
  },
  watch: {
    $route(to) {
      const menuItemName = this.routeNames[to.name];
      if (menuItemName) this.setActiveMenuItem();
    },
  },
  updated() {
    this.setActiveMenuItem();
  },
  created() {
    this.setActiveMenuItem();
    this.setIsLeftNavCollapsed(false);
  },
  methods: {
    ...uiActions(["setIsLeftNavCollapsed"]),
    setActiveMenuItem() {
      this.selectMenuItemIndex(this.$route.name);
    },
    selectMenuItemIndex(routeName) {
      const menuItemName = this.routeNames[routeName];
      if (!menuItemName) {
        console.error("Menu item not identified.");
        return;
      }

      const menuItem = this.filteredMenuItems.find(
        (item) => item.name === menuItemName
      );

      if (!menuItem) {
        console.error("Menu item not indentified.");
        return;
      }

      this.defaultActiveMenuItem = String(menuItem.index);
    },
    setCollapse(newCollapseValue) {
      this.collapse = newCollapseValue;
      this.setIsLeftNavCollapsed(newCollapseValue);
    },
  },
};
</script>

<style lang="scss">
.border-box {
  box-sizing: border-box;
}

.el-aside {
  background-color: white;
  border-right: #dee5ec 1px solid;
}

.el-menu {
  border-right: 0 solid !important;
}

.menu__container {
  display: flex;
}

.aside__normal-width {
  width: 232px !important;
  transition: width 0.5s;
}
.aside__collapsed-width {
  width: 88px !important;
  transition: width 0.5s;
}

.el-menu--collapse {
  width: 88px !important;
}

.collapsed__menu-item__container {
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
}

.flex__center {
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: row;
}
</style>
